/** Data minimization shared by browser destinations. Never export bearer routes. */
const PRIVATE_PATH =
  /(?:^|\/)(?:s|cv-share|cv-access|cv-handoff|resume-import|auth-complete|reset-password|email-preferences|verify-email|invite|invitations|callback)(?:\/|$)/i;
/**
 * A CV Health report permalink: `/r/<token>` on cvhealth.tabbio.com, rewritten
 * to `/public-cv-health/r/<token>`. Anchored at the root, not a bare `r`
 * segment anywhere, because `/en/tools/skills/r` (the R language) is a
 * public page.
 */
const PRIVATE_CV_HEALTH_REPORT_PATH = /^\/(?:public-cv-health\/)?r(?:\/|$)/i;
const CAMPAIGN_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];
const CLICK_KEYS = ["gclid", "gbraid", "wbraid", "fbclid", "ttclid"];

export function isPrivateAnalyticsPath(path: string): boolean {
  try {
    const pathname = decodeURIComponent(
      new URL(path, "https://tabbio.com").pathname,
    );
    return (
      PRIVATE_PATH.test(pathname) ||
      PRIVATE_CV_HEALTH_REPORT_PATH.test(pathname)
    );
  } catch {
    return true;
  }
}

/** Drop fragments and arbitrary query values; preserve only campaign attribution. */
export function analyticsPageUrl(
  value: string,
  base = "https://tabbio.com",
): string | null {
  try {
    const url = new URL(value, base);
    if (
      !["https:", "http:"].includes(url.protocol) ||
      isPrivateAnalyticsPath(url.pathname)
    )
      return null;
    const safe = new URL(url.origin + url.pathname);
    for (const key of [...CAMPAIGN_KEYS, ...CLICK_KEYS]) {
      const raw = url.searchParams.get(key)?.trim();
      if (!raw || raw.length > 200 || /@|https?:|[<>]/i.test(raw)) continue;
      safe.searchParams.set(
        key,
        CAMPAIGN_KEYS.includes(key) ? raw.toLowerCase() : raw,
      );
    }
    return safe.toString();
  } catch {
    return null;
  }
}

export function analyticsReferrer(value: string): string | undefined {
  try {
    const url = new URL(value);
    return ["https:", "http:"].includes(url.protocol) ? url.origin : undefined;
  } catch {
    return undefined;
  }
}

/** Allow scalar product facts, but never arbitrary text, secrets or page titles. */
export function sanitizeAnalyticsProperties(
  properties: Record<string, unknown> = {},
): Record<string, string | number | boolean> {
  const result: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(properties)) {
    const normalized = key.replace(/[^a-z0-9]/gi, "").toLowerCase();
    if (
      /token|password|secret|email|phone|fullname|firstname|lastname|cvtext|resume|message|prompt|filename|chatcontent|cvcontent|contactdetails|bearer/.test(
        normalized,
      ) ||
      ["title", "pagetitle", "ctatext", "text", "body", "name"].includes(
        normalized,
      )
    )
      continue;
    if (typeof value === "number") {
      if (Number.isFinite(value)) result[key] = value;
    } else if (typeof value === "boolean") {
      result[key] = value;
    } else if (typeof value === "string") {
      if (normalized.includes("referrer")) {
        const referrer = analyticsReferrer(value);
        if (referrer) result[key] = referrer;
      } else if (
        normalized.includes("url") ||
        normalized === "pagelocation" ||
        /^https?:\/\//i.test(value)
      ) {
        const url = analyticsPageUrl(value);
        if (url) result[key] = url;
      } else if (value.startsWith("/")) {
        const url = analyticsPageUrl(value);
        if (url) {
          const parsed = new URL(url);
          result[key] = parsed.pathname + parsed.search;
        }
      } else if (!/@|[<>]/.test(value)) {
        result[key] = value.slice(0, 200);
      }
    }
  }
  return result;
}

/** Explicitly override Google's automatic document context on every event. */
export function analyticsPageContext(
  location: string,
  referrer = "",
): Record<string, string> {
  const safeUrl = analyticsPageUrl(location);
  return {
    page_location: safeUrl ?? "",
    page_referrer: analyticsReferrer(referrer) ?? "",
    page_title: "Tabbio",
  };
}
